import { useEffect, useState } from "react";
import axios from "axios";
import { Crisp } from "crisp-sdk-web";
import { useSession } from "next-auth/react";
import { getUserID } from "@/lib/utils";
import {
  NEXT_PUBLIC_CRISP_WEBSITE_ID,
  SHOW_CRISP_WIDGET,
} from "@/services/crisp/constants";
import { isProductionEnvironment } from "@/utils/environment";
import { useAppContext } from "@/context/App-context";

const CrispWidget = () => {
  const { isCrispReady, setCrispReady } = useAppContext();
  const [isCrispConfigured, setCrispConfigured] = useState(false);
  const [isCrispConversationUpdated, setIsCrispConversationUpdated] =
    useState(false);
  const [crispSessionID, setCrispSessionID] = useState(null);
  const { data: session, status } = useSession();
  const isLoading = status === "loading";
  const isCrispEnabled = !!(SHOW_CRISP_WIDGET && NEXT_PUBLIC_CRISP_WEBSITE_ID);

  useEffect(() => {
    if (!isCrispEnabled) return;
    if (isLoading) return;

    if (isCrispReady && session) {
      const userId = getUserID(session);
      const {
        user: { email, name, image },
        crisp_meta,
        user_segment,
      } = session;
      const { signature } = crisp_meta || {};

      // Set user's information
      Crisp.setTokenId(userId);
      if (name) Crisp.user.setNickname(name);
      if (image) Crisp.user.setAvatar(image);
      if (email) {
        if (signature)
          window.$crisp.push(["set", "user:email", [email, signature]]);
        else window.$crisp.push(["set", "user:email", [email]]);
      }
    }

    if (isCrispConfigured) Crisp.load();
  }, [isCrispEnabled, isLoading, isCrispConfigured, isCrispReady, session]);

  useEffect(() => {
    if (!isCrispEnabled) return;

    Crisp.configure(NEXT_PUBLIC_CRISP_WEBSITE_ID, {
      autoload: false,
      safeMode: isProductionEnvironment(),
    });

    // If Crisp.load() executes before Crisp.configure finishes,
    // an error "websiteId must be set before loading Crisp" is raised.
    setCrispConfigured(true);

    // Callbacks when Crisp has finished setting up
    window.CRISP_READY_TRIGGER = () => {
      Crisp.toggleOperatorCount(false);
      window.$crisp.push([
        "on",
        "message:sent",
        (message) => {
          const sessionID = message?.user?.user_id;
          if (sessionID) {
            setCrispSessionID(sessionID);

            // Disable the message:sent event listener to avoid multiple calls
            window.$crisp.push(["off", "message:sent"]);
          }
        },
      ]);

      setCrispReady(true);
    };
  }, [isCrispEnabled, setCrispReady]);

  useEffect(() => {
    if (!isCrispEnabled) return;
    if (isLoading) return;
    if (!session) return;

    // Avoid updating the same metadata multiple times
    if (isCrispConversationUpdated) return;

    const userId = getUserID(session);
    const { user_segment } = session;
    if (crispSessionID) {
      // A conversation is only created on Crisp after user sends a message.
      // Even when the chat widget is loaded and the sessionID is ready,
      // Crisp API will return a 404 error if we try to update the conversation metadata.
      // We wait until the user sends a message, before making the API call
      axios
          .put("/api/services/crisp/conversations", {
            sessionId: crispSessionID,
            user: {
              userID: userId,
              userSegment: user_segment,
            },
          })
          .then(() => {});

      setIsCrispConversationUpdated(true);
    }
  }, [
    isCrispEnabled,
    isLoading,
    session,
    crispSessionID,
    isCrispConversationUpdated,
  ]);

  return null;
};

export default CrispWidget;
